/**
 * What: Draws a Worker-sent `action` block — a button that reports `{key:'press'}` or, when the Worker names a `client`
 *   action, runs that fixed browser handler. Icon-only when the Worker sends both an icon and an accessible name.
 * Transport / data-flow: Pure presentation over the `action` payload; `SectionBlocks` hands the block, locks and the
 *   client-action registry in. The button holds no icon list — `block.icon` is the contract's closed `builderActionIconSchema`.
 */
import type { BuilderBlock } from '@testfeed/shopify-contracts'
import type { ClientActions, Locks, OnEdit } from './SectionBlocks'

type Action = Extract<BuilderBlock, { kind: 'action' }>

export function ActionBlock({
  block,
  locks,
  clientActions,
  onEdit,
}: {
  block: Action
  locks: Locks
  clientActions: ClientActions
  onEdit: OnEdit
}) {
  // Icon-only when the Worker sends both an icon and an accessible name (the suggestion vote controls): the visible label is
  // then dropped so the button reads as a compact icon button, exactly like the original vote buttons.
  const iconOnly = Boolean(block.icon && block.accessibilityLabel)
  const button = (
    <s-button
      variant={block.variant}
      tone={block.tone ?? undefined}
      icon={block.icon}
      accessibilityLabel={block.accessibilityLabel ?? undefined}
      disabled={block.disabled || locks.structure}
      inlineSize={block.width === 'constrained' ? 'fill' : undefined}
      onClick={() => (block.client ? clientActions[block.client]() : onEdit(block.key, block.value ?? 'press'))}
    >
      {iconOnly ? null : block.label}
    </s-button>
  )
  // `width: 'constrained'` draws the button 280px wide (full width on phones; the original "Use this idea" sizing): the
  // wrapper takes the width and the button fills it. The constrained wrapper is the row's own flex item (no inline stack
  // around it), so its percentage widths resolve against the row, not against a content-sized stack (`SectionBlocks.css`).
  if (block.width === 'constrained') return <div className="tf-action--constrained">{button}</div>
  return <s-stack direction="inline">{button}</s-stack>
}
